<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else-if="canView || user?.is_staff" class="p-6 dark:text-white">
    <!-- Header Section -->
    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
    >
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
        Divisions - {{ filteredDivisions.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      
      <div class="mb-4 text-base flex space-x-3 justify-between">
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button v-if="canAdd || user?.is_staff"
          id="add-division-btn"
          @click="goToAddDivision"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Division
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Probes..."
      @select="handleOrgSelect"
    />

    <!-- Search and Filter Section -->
    <div class="flex flex-col gap-4 mb-6">
      <!-- Search Bar -->
      <div class="w-full">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search divisions by name..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div
        class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
      >
        <GlassSelect
          v-model="bulkAction"
          :options="canDelete || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedDivisions.length === 0"
          :class="[
            'w-full sm:w-auto btn focus:outline-none focus:ring-2 focus:ring-blue-500 text-base',
            bulkAction && selectedDivisions.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Divisions Table -->
    <GenericTable
      id="divisions-table"
      ref="table"
      :columns="columns"
      :items="paginatedDivisions"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedDivisions"
      @rowClick="goToDivisionDetail"
    >
      <!-- Custom cell templates -->
      <template #org="{ value }">
        {{ formatValue("org", value) }}
      </template>
      <template #last_modified="{ value }">
        {{ formatValue("last_modified", value) }}
      </template>
      <template #created_date="{ value }">
        {{ formatValue("created_date", value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div class="flex flex-col gap-4 mt-8">
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-2 w-full sm:w-auto">
          <button
            @click="changePage(-1)"
            :disabled="currentPage === 1"
            class="flex-1 sm:flex-initial px-6 py-3 rounded-lg bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 text-base"
          >
            Previous
          </button>
          <button
            @click="changePage(1)"
            :disabled="currentPage === totalPages"
            class="flex-1 sm:flex-initial px-6 py-3 rounded-lg bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 text-base"
          >
            Next
          </button>
        </div>

        <div class="text-gray-700 dark:text-white text-center">
          Page {{ currentPage }} of {{ totalPages }}
        </div>

        <div class="w-full sm:w-auto">
          <GlassSelect
            v-model="itemsPerPage"
            :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
            :valueKey="'value'"
            :labelKey="'label'"
            placeholder="Items per page"
            @change="updatePagination"
          />
        </div>
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
    />

  </div>
</template>

<script>
import { getAllDivisions, deleteDivision } from "@/services/divisionService";
import { getSites } from "@/services/siteService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import DeleteModal from "@/components/DeleteModal.vue";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import GlassSelect from "@/components/GlassSelect.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  name: "DivisionView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    DeleteModal,
    OrganizationTabs,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      divisions: [],
      searchQuery: "",
      filteredDivisions: [],
      currentPage: 1,
      itemsPerPage: 20,
      selectedDivisions: [],
      deletingItem: false,
      bulkAction: "",
      sites:[],
      columns: [
        { key: "name", label: "Name", sortable: true, width: "20%" },
        {
          key: "abbreviated_name",
          label: "Abbreviated Name",
          sortable: true,
          width: "15%",
        },
        { key: "code", label: "Code", sortable: true, width: "10%" },
        { key: "org", label: "Organisation", sortable: true, width: "15%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "20%",
        },
        {
          key: "created_date",
          label: "Created Date",
          sortable: true,
          width: "20%",
        },
      ],
      canAdd: false,
      canDelete: false,
      canView: false,
      error: "",
      errorCode: "",
      errorMessages: {
        400 : "Bad Request .The server could not understand the request.",
        401 : "Unauthorized access. Please check your authentication token or log in again.",
        403 : "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
        404 : "Page Not Found.",
        500 : "Internal Server Error - Something went wrong on the server.",
        422 : "Malformed request. Failed to load Division Areas!"
      },
      isDeleting: false,
      divisionTutorialSteps: [
        {
          element: '#searchbar',
          action: 'display',
          message: 'Search for divisions by name to filter the list.'
        },
        {
          element: '#add-division-btn',
          action: 'display',
          message: 'Click here to create a new division.'
        },
        {
          element: '#divisions-table',
          action: 'display',
          message: 'Click any row to view or edit division details.'
        },
      ],
    };
  },
  computed: {
    user(){
      return useStore.getUser();
    },
    isServiceProviderUser() {
      return useStore.isServiceProviderUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredDivisions.length / this.itemsPerPage);
    },
    paginatedDivisions() {
      if (this.itemsPerPage === -1) {
        return this.filteredDivisions;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredDivisions.slice(start, end);
    },
    componetObjects() {
      return this.selectedDivisions.map(division=> ({
        values:  [
          { name: "Division", value: division.name},
          { name: "Sites", value: division.related_objects.site_count},
        ]
      }));
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
      await this.tenant.syncUrlParam();
      await this.fetchDivisions(this.tenant.currentSchemaName.value);
    } else {
      await this.tenant.loadInitialData(this.fetchDivisions);
    }
    this.applyFilters();
  },
  methods: {
    async startTutorial() {
      await this._start(this.divisionTutorialSteps);
    },
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add division");
        this.canDelete = await useStore.hasPermission("Can delete division");
        this.canView = await useStore.hasPermission("Can view division");
      }
    },
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchDivisions);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error('Organization select error:', error);
      }
    },
    goToAddDivision(){
      this.$router.push({
        name: 'AddDivision',
        query: this.tenant.addSchemaParam()
      })
    },
    async fetchDivisions(schemaName=null) {
      try {
        if (!schemaName) {
          const schemaName = this.tenant.currentSchemaName.value;
          if (!schemaName) {
            this.error = "Malformed request. Failed to load divisions!";
            this.errorCode = "422!";
            return;
          }
        }
        this.divisions = await getAllDivisions(schemaName);
        this.applyFilters();
      } catch (error) {
        if (error.response) {
          this.error = `${this.errorMessages[error.response.status]}`
          this.errorCode = `${error.response.status}`
        } else{
          this.error = `Unknown Error`
        }
      }
    },
    goToDivisionDetail(division) {
      this.$router.push({
        name: "DivisionDetails",
        params: { id: division.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    applyFilters() {
      this.filteredDivisions = this.divisions.filter((division) =>
        division.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
      this.currentPage = 1;
    },
    updateSelectedDivisions(selectedItems) {
      this.selectedDivisions = selectedItems;
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true
      await this.deleteSelectedDivisions();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedDivisions() {
      if (this.selectedDivisions.length === 0) {
        alert("No divisions selected for deletion");
        return;
      }
      const schemaName = this.tenant.currentSchemaName.value;
      if (!schemaName) {
        this.error = "Malformed request. Failed to delete selected divisions!";
        this.errorCode = "422!";
        return;
      }

      try {
        const deletePromises = this.selectedDivisions.map((division) =>
          deleteDivision(division.id, schemaName)
        );
        await Promise.all(deletePromises);

        this.divisions = this.divisions.filter(
          (division) => !this.selectedDivisions.some((item) => item.id === division.id)
        );
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedDivisions = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting divisions. Please try again.");
      }  finally {
        this.isDeleting = false;
      }
    },
    formatValue(key, value) {
      if (key === "org") {
        return this.tenant.getOrganizationName(value) || "redacted";
      }
      if (key === "last_modified" || key === "created_date") {
        return this.formatDateTime(value);
      }
      return value !== null && value !== undefined ? value : "N/A";
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
  },
};
</script>
